// Location + Sublocation masters management.
//
// Two panels: pick a Location on the left to manage its Sublocations on the
// right (cascading). Feeds the Country/State/Location/Sublocation dropdowns on
// the client form. Data via the location/sublocation hooks; mutations
// invalidate the relevant keys.
import { useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardLayout from "../layouts/DashboardLayout";
import { useThemeColors } from "../context/ThemeContext";
import GlassCard from "../components/GlassCard";
import { HEADING_FONT } from "../theme/fonts";
import {
  useLocations,
  useSublocations,
  useCreateLocation,
  useUpdateLocation,
  useDeleteLocation,
  useCreateSublocation,
  useUpdateSublocation,
  useDeleteSublocation,
} from "../hooks/queries";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { AuthContext } from "../context/AuthContext";

const GRADIENT = "linear-gradient(135deg, #818cf8 0%, #6366f1 100%)";

function Locations() {
  const colors = useThemeColors();
  const toast = useToast();
  const confirm = useConfirm();

  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("locations", "add");
  const canEdit = hasPermission("locations", "edit");
  const canDelete = hasPermission("locations", "delete");

  const { data: locations = [] } = useLocations();
  const [selectedLoc, setSelectedLoc] = useState(null);
  const { data: sublocations = [] } = useSublocations(selectedLoc?.id);

  const createLoc = useCreateLocation();
  const updateLoc = useUpdateLocation();
  const deleteLoc = useDeleteLocation();
  const createSub = useCreateSublocation();
  const updateSub = useUpdateSublocation();
  const deleteSub = useDeleteSublocation();

  // Inline add inputs
  const [newLoc, setNewLoc] = useState("");
  const [newSub, setNewSub] = useState("");

  async function addLocation() {
    const name = newLoc.trim();
    if (!name) return;
    try {
      await createLoc.mutateAsync({ name });
      setNewLoc("");
      toast.success("Location added");
    } catch (err) {
      toast.error(err.message || "Failed to add location");
    }
  }

  async function renameLocation(loc) {
    const name = window.prompt("Rename location", loc.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === loc.name) return;
    try {
      await updateLoc.mutateAsync({ id: loc.id, data: { name: trimmed } });
      toast.success("Location updated");
    } catch (err) {
      toast.error(err.message || "Failed to update location");
    }
  }

  async function removeLocation(loc) {
    const ok = await confirm({
      title: "Delete location?",
      message: `"${loc.name}" and all its sublocations will be removed.`,
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await deleteLoc.mutateAsync(loc.id);
      if (selectedLoc?.id === loc.id) setSelectedLoc(null);
      toast.success("Location deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete location");
    }
  }

  async function addSublocation() {
    const name = newSub.trim();
    if (!name || !selectedLoc) return;
    try {
      await createSub.mutateAsync({ locationId: selectedLoc.id, name });
      setNewSub("");
      toast.success("Sublocation added");
    } catch (err) {
      toast.error(err.message || "Failed to add sublocation");
    }
  }

  async function renameSublocation(sub) {
    const name = window.prompt("Rename sublocation", sub.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === sub.name) return;
    try {
      await updateSub.mutateAsync({ id: sub.id, data: { name: trimmed } });
      toast.success("Sublocation updated");
    } catch (err) {
      toast.error(err.message || "Failed to update sublocation");
    }
  }

  async function removeSublocation(sub) {
    const ok = await confirm({
      title: "Delete sublocation?",
      message: `"${sub.name}" will be removed.`,
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await deleteSub.mutateAsync({ id: sub.id, locationId: selectedLoc.id });
      toast.success("Sublocation deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete sublocation");
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#818cf8" },
    },
    "& input": { color: colors.text },
  };

  const addBtnSx = {
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderRadius: 2,
    color: "#fff",
    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
    "&:hover": { boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.6)", background: colors.cardBorder },
  };

  function RowItem({ item, onEdit, onDelete, active, onClick, showChevron }) {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          cursor: onClick ? "pointer" : "default",
          borderBottom: `1px solid ${colors.divider}`,
          background: active ? "rgba(129,140,248,0.12)" : "transparent",
          "&:hover": { background: active ? "rgba(129,140,248,0.16)" : colors.hover },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            background: GRADIENT,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography sx={{ flex: 1, fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}>
          {item.name}
        </Typography>
        {canEdit && (
          <Tooltip title="Rename">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(item); }} sx={{ color: colors.textMuted, "&:hover": { color: "#818cf8" } }}>
              <EditOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(item); }} sx={{ color: colors.textMuted, "&:hover": { color: "#ef4444" } }}>
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
        {showChevron && <ChevronRightIcon sx={{ fontSize: 18, color: active ? "#818cf8" : colors.textFaint }} />}
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: HEADING_FONT, fontWeight: 700, color: colors.text, letterSpacing: "-0.5px" }}>
          Locations
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
          Manage locations and their sublocations — used in client geography
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        {/* Locations panel */}
        <GlassCard sx={{ flex: 1, overflow: "hidden", width: "100%" }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${colors.divider}`, background: colors.hover }}>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: colors.textMuted }}>
              Locations
            </Typography>
          </Box>
          {canAdd && (
            <Stack direction="row" spacing={1} sx={{ p: 2, borderBottom: `1px solid ${colors.divider}` }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Add a location…"
                value={newLoc}
                onChange={(e) => setNewLoc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addLocation()}
                sx={fieldSx}
              />
              <Button startIcon={<AddIcon />} onClick={addLocation} disabled={!newLoc.trim()} sx={addBtnSx}>
                Add
              </Button>
            </Stack>
          )}
          {locations.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", color: colors.textMuted, fontSize: 13 }}>No locations yet</Box>
          ) : (
            locations.map((loc, idx) => (
              <RowItem
                key={loc.id}
                item={loc}
                idx={idx}
                active={selectedLoc?.id === loc.id}
                onClick={() => setSelectedLoc(loc)}
                onEdit={renameLocation}
                onDelete={removeLocation}
                showChevron
              />
            ))
          )}
        </GlassCard>

        {/* Sublocations panel */}
        <GlassCard sx={{ flex: 1, overflow: "hidden", width: "100%" }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${colors.divider}`, background: colors.hover }}>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: colors.textMuted }}>
              {selectedLoc ? `Sublocations of ${selectedLoc.name}` : "Sublocations"}
            </Typography>
          </Box>
          {!selectedLoc ? (
            <Box sx={{ py: 6, textAlign: "center", color: colors.textMuted, fontSize: 13 }}>
              Select a location to manage its sublocations
            </Box>
          ) : (
            <>
              {canAdd && (
                <Stack direction="row" spacing={1} sx={{ p: 2, borderBottom: `1px solid ${colors.divider}` }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add a sublocation…"
                    value={newSub}
                    onChange={(e) => setNewSub(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addSublocation()}
                    sx={fieldSx}
                  />
                  <Button startIcon={<AddIcon />} onClick={addSublocation} disabled={!newSub.trim()} sx={addBtnSx}>
                    Add
                  </Button>
                </Stack>
              )}
              {sublocations.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center", color: colors.textMuted, fontSize: 13 }}>No sublocations yet</Box>
              ) : (
                sublocations.map((sub) => (
                  <RowItem key={sub.id} item={sub} onEdit={renameSublocation} onDelete={removeSublocation} />
                ))
              )}
            </>
          )}
        </GlassCard>
      </Stack>
    </DashboardLayout>
  );
}

export default Locations;
